import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Alert,
    Box,
    Button,
    Card,
    Checkbox,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Popover,
    Select,
    Snackbar,
    Stack,
    Switch,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import i18n from '../../../i18';
import config from '../../../config';
import ConfirmDialog from '../../dialogs/ConfirmDialog';
import Iconify from '../../../components/iconify';
import { HeaderWidget } from '../../../sections/header/HeaderWidget';
import { MonitorMobbileBulkIcon } from '../../../icons-lib/bulkIcons';
import { formatError } from '../../../utils/formatError';

const AUDIENCE_OPTIONS = ['all', 'user', 'merchant'];
const ACTIVE_FILTER_OPTIONS = ['all', 'true', 'false'];
const LOCALE_FIELDS = [
    { key: 'de', label: 'Deutsch - de' },
    { key: 'en', label: 'English - en' },
    { key: 'it', label: 'Italiano - it' },
    { key: 'fr', label: 'Francais - fr' }
];

const createLocalizedValues = () => ({
    de: '',
    en: '',
    it: '',
    fr: ''
});

const createDefaultForm = () => ({
    title: createLocalizedValues(),
    body: createLocalizedValues(),
    audience: 'all',
    isActive: true
});

function toLocalizedState(value) {
    const parsed = parseJsonSafe(value) || {};
    return {
        de: typeof parsed.de === 'string' ? parsed.de : '',
        en: typeof parsed.en === 'string' ? parsed.en : '',
        it: typeof parsed.it === 'string' ? parsed.it : '',
        fr: typeof parsed.fr === 'string' ? parsed.fr : ''
    };
}

function serializeLocalizedValues(values) {
    const prepared = Object.entries(values || {}).reduce((acc, [key, value]) => {
        if (typeof value === 'string' && value.trim()) {
            acc[key] = value.trim();
        }
        return acc;
    }, {});

    return JSON.stringify(prepared);
}

const DEFAULT_SORT = {
    field: 'dateModified',
    direction: 'DESC'
};

function t(key, fallback) {
    const value = i18n.t(key);
    if (!value || value === key) {
        return fallback;
    }
    return value;
}

function parseJsonSafe(value) {
    if (value === null || value === undefined) return null;

    if (typeof value === 'object') {
        return value;
    }

    if (typeof value !== 'string' || !value.trim()) {
        return null;
    }

    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
}

function pickLocalized(jsonData, language) {
    const parsed = parseJsonSafe(jsonData);

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return '';
    }

    const fallbackLanguage = config.language || 'en';
    return (
        parsed[language] ||
        parsed[fallbackLanguage] ||
        parsed.en ||
        Object.values(parsed).find((value) => typeof value === 'string' && value.trim()) ||
        ''
    );
}

function buildPreview(jsonData, language, maxLength = 72) {
    const localized = pickLocalized(jsonData, language);
    if (!localized) return '-';
    if (localized.length <= maxLength) return localized;
    return `${localized.slice(0, maxLength)}...`;
}

function formatDate(value) {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return `${value}`;

    return date.toLocaleString();
}

function normalizeListResult(result) {
    if (Array.isArray(result)) {
        return {
            error: 'OK',
            data: result,
            total_count: result.length
        };
    }

    if (Array.isArray(result?.data)) {
        return {
            error: result.error || 'OK',
            data: result.data,
            total_count: result.total_count || result.data.length
        };
    }

    return {
        error: result?.error || 'error',
        data: [],
        total_count: 0,
        raw: result
    };
}

export default function SystemMessagesPage(props) {
    const { context } = props;
    const server = context.getState().server.systemMessages;

    const [activeTab, setActiveTab] = useState(0);

    const [list, setList] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [listLoading, setListLoading] = useState(true);
    const [listError, setListError] = useState('');

    const [sortConfig, setSortConfig] = useState(DEFAULT_SORT);
    const [filters, setFilters] = useState({
        audience: 'all',
        isActive: 'all',
        search: ''
    });

    const [selectedIds, setSelectedIds] = useState([]);

    const [formData, setFormData] = useState(createDefaultForm);
    const [formErrors, setFormErrors] = useState({});
    const [submitLoading, setSubmitLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [jsonDialog, setJsonDialog] = useState({
        open: false,
        title: '',
        payload: {}
    });

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [actionMenu, setActionMenu] = useState({
        anchorEl: null,
        item: null
    });

    const [previewSettings, setPreviewSettings] = useState({
        audience: 'user',
        language: localStorage.language || config.language || 'en'
    });

    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        severity: 'success',
        message: ''
    });

    const showToast = useCallback((message, severity = 'success') => {
        setSnackbar({
            open: true,
            severity,
            message
        });
    }, []);

    const hideToast = useCallback(() => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    }, []);

    const applyClientFilters = useCallback(
        (items) => {
            const searchTerm = filters.search.trim().toLowerCase();

            return items.filter((item) => {
                const audienceMatch =
                    filters.audience === 'all' || item.audience === filters.audience;

                const activeMatch =
                    filters.isActive === 'all' ||
                    `${Boolean(item.isActive)}` === filters.isActive;

                if (!searchTerm) {
                    return audienceMatch && activeMatch;
                }

                const titleText = pickLocalized(item.title, previewSettings.language)
                    .toLowerCase()
                    .trim();
                const bodyText = pickLocalized(item.body, previewSettings.language)
                    .toLowerCase()
                    .trim();

                const searchMatch =
                    `${item.id}`.includes(searchTerm) ||
                    titleText.includes(searchTerm) ||
                    bodyText.includes(searchTerm);

                return audienceMatch && activeMatch && searchMatch;
            });
        },
        [filters, previewSettings.language]
    );

    const retrieveList = useCallback(async () => {
        setListLoading(true);
        setListError('');

        const backendFilter = [];

        if (filters.audience !== 'all') {
            backendFilter.push({ field: 'audience', op: ':', value: filters.audience });
        }

        if (filters.isActive !== 'all') {
            backendFilter.push({
                field: 'isActive',
                op: ':',
                value: filters.isActive === 'true'
            });
        }

        try {
            const sort = server.getSortField(sortConfig.field, sortConfig.direction);

            const result = await server.getAllFiltered(backendFilter, sort, {
                paging: {
                    page: 0,
                    pageSize: 200
                }
            });

            const normalized = normalizeListResult(result);

            if (normalized.error !== 'OK') {
                const text = formatError(normalized.raw || result);
                setListError(text || i18n.t('errors.somethingWentWrong'));
                setList([]);
                setTotalCount(0);
                return;
            }

            const filtered = applyClientFilters(normalized.data);

            setList(filtered);
            setTotalCount(filtered.length);

            setSelectedIds((prev) => prev.filter((id) => filtered.some((item) => item.id === id)));
        } catch (error) {
            setListError(formatError(error));
            setList([]);
            setTotalCount(0);
        } finally {
            setListLoading(false);
        }
    }, [applyClientFilters, filters.audience, filters.isActive, server, sortConfig]);

    useEffect(() => {
        retrieveList();
    }, [retrieveList]);

    const currentLanguage = previewSettings.language;

    const selectedCount = selectedIds.length;

    const isAllSelected = useMemo(() => {
        return list.length > 0 && selectedIds.length === list.length;
    }, [list.length, selectedIds.length]);

    const mobileStatusPayload = useMemo(() => {
        const titleJson = formData.title || {};
        const bodyJson = formData.body || {};

        return {
            status: formData.isActive ? 'ACTIVE' : 'INACTIVE',
            audience: previewSettings.audience,
            language: previewSettings.language,
            message: {
                id: editingId,
                audience: formData.audience,
                isActive: formData.isActive,
                title: pickLocalized(titleJson, previewSettings.language),
                body: pickLocalized(bodyJson, previewSettings.language),
                titleI18n: titleJson,
                bodyI18n: bodyJson
            }
        };
    }, [editingId, formData, previewSettings.audience, previewSettings.language]);

    const validateLocalizedField = (fieldName, value) => {
        const hasNonEmptyValue = Object.values(value || {}).some(
            (item) => typeof item === 'string' && item.trim().length > 0
        );

        if (!hasNonEmptyValue) {
            return `${t(`columns.${fieldName}`, fieldName)} ${t('validation.required', 'is required')}`;
        }

        return null;
    };

    const validateForm = () => {
        const errors = {};

        const titleError = validateLocalizedField('title', formData.title);
        if (titleError) errors.title = titleError;

        const bodyError = validateLocalizedField('body', formData.body);
        if (bodyError) errors.body = bodyError;

        if (!AUDIENCE_OPTIONS.includes(formData.audience)) {
            errors.audience = t(
                'validation.invalidAudience',
                'Audience must be all, user, or merchant'
            );
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const resetForm = () => {
        setEditingId(null);
        setFormErrors({});
        setFormData(createDefaultForm());
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        const payload = {
            title: serializeLocalizedValues(formData.title),
            body: serializeLocalizedValues(formData.body),
            audience: formData.audience,
            isActive: Boolean(formData.isActive)
        };

        setSubmitLoading(true);

        try {
            const result = editingId
                ? await server.update(editingId, payload)
                : await server.add(payload);

            if (result.error !== 'OK') {
                showToast(formatError(result), 'error');
                return;
            }

            showToast(
                editingId
                    ? t('messages.systemMessageUpdated', 'System message updated successfully.')
                    : t('messages.systemMessageCreated', 'System message created successfully.'),
                'success'
            );

            resetForm();
            setActiveTab(0);
            await retrieveList();
        } catch (error) {
            showToast(formatError(error), 'error');
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleOpenEdit = (item) => {
        const title = toLocalizedState(item.title);
        const body = toLocalizedState(item.body);

        setEditingId(item.id);
        setFormErrors({});
        setFormData({
            title,
            body,
            audience: item.audience || 'all',
            isActive: Boolean(item.isActive)
        });
        setActiveTab(1);
    };

    const handleDelete = async () => {
        const ids = selectedIds.length > 0 ? selectedIds : [];

        if (!ids.length) {
            setDeleteDialogOpen(false);
            return;
        }

        try {
            const result = await server.delete(ids);
            if (result.error !== 'OK') {
                showToast(formatError(result), 'error');
                return;
            }

            showToast(
                t('messages.systemMessageDeleted', 'System message deleted successfully.'),
                'success'
            );
            setSelectedIds([]);
            setDeleteDialogOpen(false);
            await retrieveList();
        } catch (error) {
            showToast(formatError(error), 'error');
        }
    };

    const handleBulkDeleteClick = () => {
        if (!selectedIds.length) {
            return;
        }
        setDeleteDialogOpen(true);
    };

    const handleOpenActionMenu = (event, item) => {
        setActionMenu({
            anchorEl: event.currentTarget,
            item
        });
    };

    const handleCloseActionMenu = () => {
        setActionMenu({
            anchorEl: null,
            item: null
        });
    };

    const setSort = (field) => {
        setSortConfig((prev) => {
            if (prev.field !== field) {
                return {
                    field,
                    direction: 'DESC'
                };
            }

            return {
                field,
                direction: prev.direction === 'DESC' ? 'ASC' : 'DESC'
            };
        });
    };

    const renderSortLabel = (field, label) => {
        const isCurrent = sortConfig.field === field;
        const direction = isCurrent ? sortConfig.direction : '';

        return (
            <Button
                onClick={() => setSort(field)}
                size="small"
                color={isCurrent ? 'primary' : 'inherit'}
            >
                {label} {direction ? `(${direction})` : ''}
            </Button>
        );
    };

    const renderList = () => {
        return (
            <Card sx={{ mt: 3 }}>
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        gap: 2,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>{t('columns.audience', 'Audience')}</InputLabel>
                            <Select
                                label={t('columns.audience', 'Audience')}
                                value={filters.audience}
                                onChange={(event) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        audience: event.target.value
                                    }))
                                }
                            >
                                {AUDIENCE_OPTIONS.map((item) => (
                                    <MenuItem key={item} value={item}>
                                        {item}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>{i18n.t('columns.isActive')}</InputLabel>
                            <Select
                                label={i18n.t('columns.isActive')}
                                value={filters.isActive}
                                onChange={(event) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        isActive: event.target.value
                                    }))
                                }
                            >
                                {ACTIVE_FILTER_OPTIONS.map((item) => (
                                    <MenuItem key={item} value={item}>
                                        {item}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            size="small"
                            label={t('text.search', 'Search')}
                            placeholder={t('placeholders.searchSystemMessages', 'Search by id/title/body')}
                            value={filters.search}
                            onChange={(event) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    search: event.target.value
                                }))
                            }
                            sx={{ minWidth: 280 }}
                        />
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Chip label={`${totalCount} ${t('text.results', 'Results')}`} />
                        <Button
                            variant="outlined"
                            onClick={() => {
                                resetForm();
                                setActiveTab(1);
                            }}
                        >
                            {t('pages.systemMessages.add', 'Create System Message')}
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            disabled={!selectedCount}
                            onClick={handleBulkDeleteClick}
                        >
                            {i18n.t('buttons.delete')} ({selectedCount})
                        </Button>
                    </Stack>
                </Box>

                {listError ? (
                    <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
                        {listError}
                    </Alert>
                ) : null}

                <TableContainer>
                    <Table sx={{ tableLayout: 'fixed' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={isAllSelected}
                                        indeterminate={selectedCount > 0 && !isAllSelected}
                                        onChange={(event) => {
                                            if (event.target.checked) {
                                                setSelectedIds(list.map((item) => item.id));
                                            } else {
                                                setSelectedIds([]);
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: 90 }}>{renderSortLabel('id', t('columns.id', 'ID'))}</TableCell>
                                <TableCell>{t('columns.title', 'Title')}</TableCell>
                                <TableCell sx={{ width: 120 }}>{t('columns.audience', 'Audience')}</TableCell>
                                <TableCell sx={{ width: 110 }}>{t('columns.isActive', 'Status')}</TableCell>
                                <TableCell sx={{ width: 180 }}>{t('columns.dateCreated', 'Date Created')}</TableCell>
                                <TableCell sx={{ width: 180 }}>
                                    {renderSortLabel(
                                        'dateModified',
                                        t('columns.dateModified', 'Date Modified')
                                    )}
                                </TableCell>
                                <TableCell sx={{ width: 80 }} align="right">
                                    {t('columns.actions', 'Actions')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {listLoading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : null}

                            {!listLoading && list.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                        <Typography variant="subtitle1">
                                            {t('text.noResults', 'No system messages found')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : null}

                            {!listLoading &&
                                list.map((item) => {
                                    const selected = selectedIds.includes(item.id);
                                    return (
                                        <TableRow key={item.id} hover selected={selected}>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selected}
                                                    onChange={() => {
                                                        setSelectedIds((prev) => {
                                                            if (prev.includes(item.id)) {
                                                                return prev.filter((id) => id !== item.id);
                                                            }
                                                            return [...prev, item.id];
                                                        });
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {item.id}
                                            </TableCell>
                                            <TableCell>
                                                <Stack spacing={0.8}>
                                                    <Typography
                                                        variant="body2"
                                                        noWrap
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                    >
                                                        {buildPreview(item.title, currentLanguage)}
                                                    </Typography>
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        onClick={() =>
                                                            setJsonDialog({
                                                                open: true,
                                                                title: `Title JSON #${item.id}`,
                                                                payload: parseJsonSafe(item.title) || item.title
                                                            })
                                                        }
                                                    >
                                                        {t('buttons.viewJson', 'View JSON')}
                                                    </Button>
                                                </Stack>
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {item.audience || '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    color={item.isActive ? 'success' : 'default'}
                                                    label={
                                                        item.isActive
                                                            ? t('pages.systemMessages.enabled', 'Enabled')
                                                            : t('pages.systemMessages.disabled', 'Disabled')
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {formatDate(item.dateCreated)}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {formatDate(item.dateModified)}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    onClick={(event) => handleOpenActionMenu(event, item)}
                                                >
                                                    <Iconify icon="eva:more-vertical-fill" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        );
    };

    const renderEditor = () => {
        return (
            <Card sx={{ mt: 3, p: 3 }}>
                <Stack spacing={2}>
                    <Typography variant="h6">
                        {editingId
                            ? `${t('pages.systemMessages.edit', 'Edit System Message')} #${editingId}`
                            : t('pages.systemMessages.add', 'Create System Message')}
                    </Typography>

                    <Card variant="outlined" sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="subtitle1">{t('columns.isActive', 'Status')}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {formData.isActive ? 'Active' : 'Inactive'}
                                </Typography>
                            </Box>
                            <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                    <Switch
                                        checked={Boolean(formData.isActive)}
                                        onChange={(event) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                isActive: event.target.checked
                                            }))
                                        }
                                    />
                                }
                                label=""
                            />
                        </Stack>
                    </Card>

                    <Card variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                            <Typography variant="subtitle1">{t('columns.title', 'Title')}</Typography>
                            {LOCALE_FIELDS.map((locale) => (
                                <TextField
                                    key={`title-${locale.key}`}
                                    label={locale.label}
                                    value={formData.title[locale.key]}
                                    onChange={(event) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            title: {
                                                ...prev.title,
                                                [locale.key]: event.target.value
                                            }
                                        }))
                                    }
                                />
                            ))}
                            {formErrors.title ? (
                                <Alert severity="error">{formErrors.title}</Alert>
                            ) : null}
                        </Stack>
                    </Card>

                    <Card variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                            <Typography variant="subtitle1">{t('columns.body', 'Body')}</Typography>
                            {LOCALE_FIELDS.map((locale) => (
                                <TextField
                                    key={`body-${locale.key}`}
                                    label={locale.label}
                                    multiline
                                    rows={3}
                                    value={formData.body[locale.key]}
                                    onChange={(event) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            body: {
                                                ...prev.body,
                                                [locale.key]: event.target.value
                                            }
                                        }))
                                    }
                                />
                            ))}
                            {formErrors.body ? (
                                <Alert severity="error">{formErrors.body}</Alert>
                            ) : null}
                        </Stack>
                    </Card>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <FormControl fullWidth error={Boolean(formErrors.audience)}>
                            <InputLabel>{t('columns.audience', 'Audience')}</InputLabel>
                            <Select
                                label={t('columns.audience', 'Audience')}
                                value={formData.audience}
                                onChange={(event) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        audience: event.target.value
                                    }))
                                }
                            >
                                {AUDIENCE_OPTIONS.map((item) => (
                                    <MenuItem key={item} value={item}>
                                        {item}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    <Card variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                            <Typography variant="subtitle1">
                                {t(
                                    'buttons.previewAsMobileStatusPayload',
                                    'Preview as mobile status payload'
                                )}
                            </Typography>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <FormControl sx={{ minWidth: 160 }}>
                                    <InputLabel>{t('columns.audience', 'Audience')}</InputLabel>
                                    <Select
                                        value={previewSettings.audience}
                                        label={t('columns.audience', 'Audience')}
                                        onChange={(event) =>
                                            setPreviewSettings((prev) => ({
                                                ...prev,
                                                audience: event.target.value
                                            }))
                                        }
                                    >
                                        {AUDIENCE_OPTIONS.filter((item) => item !== 'all').map((item) => (
                                            <MenuItem key={item} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    label={t('columns.language', 'Language')}
                                    value={previewSettings.language}
                                    onChange={(event) =>
                                        setPreviewSettings((prev) => ({
                                            ...prev,
                                            language: event.target.value
                                        }))
                                    }
                                    sx={{ minWidth: 160 }}
                                />

                                <Button
                                    variant="outlined"
                                    onClick={() => setPreviewDialogOpen(true)}
                                >
                                    {t('buttons.preview', 'Preview')}
                                </Button>
                            </Stack>
                        </Stack>
                    </Card>

                    <Stack direction="row" spacing={1.5}>
                        <LoadingButton
                            variant="contained"
                            onClick={handleSave}
                            loading={submitLoading}
                        >
                            {editingId ? t('buttons.save', 'Save') : t('buttons.create', 'Create')}
                        </LoadingButton>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                resetForm();
                                setActiveTab(0);
                            }}
                            disabled={submitLoading}
                        >
                            {i18n.t('buttons.cancel')}
                        </Button>
                    </Stack>
                </Stack>
            </Card>
        );
    };

    return (
        <>
            <Helmet>
                <title>{t('pages.systemMessages.title', 'System Messages')}</title>
            </Helmet>

            <Container maxWidth={false}>
                <Box mb={3}>
                    <HeaderWidget
                        title={t('pages.systemMessages.title', 'System Messages')}
                        subtitle={t(
                            'pages.systemMessages.subtitle',
                            'Create and manage mobile system notifications with audience and localization controls.'
                        )}
                        startIcon={MonitorMobbileBulkIcon}
                        image={config?.bannerImages?.transactions || ''}
                    />
                </Box>

                <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value)}>
                    <Tab label={t('tabs.list', 'List')} />
                    <Tab
                        label={
                            editingId
                                ? t('tabs.edit', 'Edit')
                                : t('tabs.create', 'Create')
                        }
                    />
                </Tabs>

                {activeTab === 0 ? renderList() : null}
                {activeTab === 1 ? renderEditor() : null}
            </Container>

            <Popover
                open={Boolean(actionMenu.anchorEl)}
                anchorEl={actionMenu.anchorEl}
                onClose={handleCloseActionMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        'p': 1,
                        'minWidth': 150,
                        'border': '1px solid #D0D5DD',
                        '& .MuiMenuItem-root': {
                            'px': 1,
                            'typography': 'body2',
                            'borderRadius': 0.75
                        }
                    }
                }}
            >
                <MenuItem
                    onClick={() => {
                        if (actionMenu.item) {
                            handleOpenEdit(actionMenu.item);
                        }
                        handleCloseActionMenu();
                    }}
                >
                    {i18n.t('buttons.edit')}
                </MenuItem>
                <MenuItem
                    sx={{ color: 'error.main' }}
                    onClick={() => {
                        if (actionMenu.item) {
                            setSelectedIds([actionMenu.item.id]);
                            setDeleteDialogOpen(true);
                        }
                        handleCloseActionMenu();
                    }}
                >
                    {i18n.t('buttons.delete')}
                </MenuItem>
            </Popover>

            <Dialog
                open={jsonDialog.open}
                onClose={() => setJsonDialog({ open: false, title: '', payload: {} })}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>{jsonDialog.title}</DialogTitle>
                <DialogContent>
                    <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'monospace' }}>
                        {JSON.stringify(jsonDialog.payload, null, 2)}
                    </pre>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setJsonDialog({ open: false, title: '', payload: {} })}
                    >
                        {i18n.t('buttons.close')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={previewDialogOpen}
                onClose={() => setPreviewDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {t('buttons.previewAsMobileStatusPayload', 'Preview as mobile status payload')}
                </DialogTitle>
                <DialogContent>
                    <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'monospace' }}>
                        {JSON.stringify(mobileStatusPayload, null, 2)}
                    </pre>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewDialogOpen(false)}>
                        {i18n.t('buttons.close')}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                dialogOpen={deleteDialogOpen}
                type="delete"
                onCancel={() => setDeleteDialogOpen(false)}
                onConfirm={handleDelete}
                title={i18n.t('dialog.deleteTitle') || 'Delete system message(s)?'}
                subtitle={
                    i18n.t('dialog.deleteSubtitle') ||
                    'This action will permanently remove selected records.'
                }
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={hideToast}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={hideToast} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}
